import {
  Box,
  Button,
  ResponsiveDialog,
  ResponsiveDialogActions,
  ResponsiveDialogContent,
  ResponsiveDialogTitle,
  Stack,
  Typography
} from '@dtx-company/design-system/src'
import { ContactUsPlanType } from '../LockedFeatureModal'
import { FC } from 'react'
import { LockedFeaturesUpsellContentType } from '../../../constants/lockedFeatures'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getPlanTypeName } from '../../../utils/getPlanTypeName'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useIsBillingAdmin } from '@dtx-company/inter-app/src/hooks/useIsBillingAdmin/useIsBillingAdmin'
import Image from 'next/image'
import useTranslation from 'next-translate/useTranslation'

type ContactSalesModalProps = {
  open: boolean
  onClose: () => void
  lockPlanType: ContactUsPlanType
  lockContent?: LockedFeaturesUpsellContentType
}
export const UPGRADE_LINK = 'https://meetings.hubspot.com/team-flowcode/chat-with-our-team'

export const ContactSalesModal: FC<ContactSalesModalProps> = ({
  open,
  onClose,
  lockContent,
  lockPlanType
}) => {
  const { t } = useTranslation('lock-modal')
  const { t: tCommon } = useTranslation('common')
  const planTypeName = getPlanTypeName(lockPlanType)
  const { codeLimit, pageLimit } = useAuthState()
  const isBillingAdmin = useIsBillingAdmin()

  const header = lockContent?.[lockPlanType]?.title
  const description = lockContent?.[lockPlanType]?.description(codeLimit, pageLimit, isBillingAdmin)
  const hrefObject = lockContent?.[lockPlanType]?.href?.(isBillingAdmin)
  const cta = lockContent?.[lockPlanType]?.cta?.(isBillingAdmin)
  const image = lockContent?.[lockPlanType]?.src || lockContent?.src

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth={'sm'}
      showCloseIcon
      data-testid={'contact-sales-dialog'}
    >
      <Box sx={{ px: { md: 5 } }}>
        <ResponsiveDialogTitle
          title={
            header ||
            t(
              'genericTitle',
              { planTypeName, featureDescription: lockContent?.header || 'for more features.' },
              {
                default: `Upgrade to ${planTypeName} ${lockContent?.header || 'for more features'}.`
              }
            )
          }
        />
        <ResponsiveDialogContent>
          <Stack
            gap={8}
            sx={theme => ({
              '& img': {
                width: 'auto',
                height: 'auto',
                alignSelf: 'center',
                maxHeight: theme.spacing(75)
              },
              overflow: ['hidden', 'unset', 'unset']
            })}
          >
            {description && <Typography variant={'subtitleSmall'}>{description}</Typography>}
            {image && <Image src={image} alt={'Unlock more features'} width={550} height={250} />}
          </Stack>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Stack direction={'row'} gap={2}>
            <Button variant={'text'} size={'large'} onClick={onClose}>
              {tCommon('DialogActions.Cancel', {
                default: 'Cancel'
              })}
            </Button>
            <Button
              size={'large'}
              hrefObject={hrefObject ? hrefObject : undefined}
              href={hrefObject ? undefined : UPGRADE_LINK}
              target={hrefObject ? '_self' : '_blank'}
              onClick={() => {
                fireAnalyticsEvent(
                  `ContactSalesModal_lock_modal_${cta || 'contactsales'}_Clicked`,
                  {
                    billingType: planTypeName
                  }
                )
              }}
            >
              {cta ||
                t('contactUs.submitButton', {
                  default: 'Contact Sales'
                })}
            </Button>
          </Stack>
        </ResponsiveDialogActions>
      </Box>
    </ResponsiveDialog>
  )
}
